<template>
    <div class="branch-steps" :style="cssVars">
        <div class="steps-container">
            <svg class="node-lines" ref="svgLines"></svg>
            <div style="display: flex; width: 100%;">
                <!-- 列容器 -->
                <div v-for="colIndex in data.rows" :key="'col-' + colIndex" :style="{
                    'flex-shrink': 1,
                    position: 'relative',
                    'flex-basis': 1 / data.rows[data.rows.length - 1] * 100 + '%',
                    display: 'inline-flex',
                    'flex-direction': 'column',
                }" :class="{ 'is-flex': colIndex === data.rows[data.rows.length - 1] }">
                    <!-- 行容器 -->
                    <div v-for="rowIndex in data.columns" :key="'row-' + rowIndex"
                        style="display: inline-block; width: 100%;white-space: nowrap;">
                        <!-- 节点 -->
                        <div v-for="node in data.nodes.filter(n => n.row === rowIndex && n.column === colIndex)"
                            :key="node.id" class="step-node" :class="'step-' + node.status"
                            style="z-index: 1;width: 100%;margin-bottom: 14px;">
                            <div class="step-icon-container" :id="node.id">
                                <template v-if="node.status === 'finish' || node.status === 'success'"><i
                                        class="el-step__icon-inner is-status el-icon-check"></i></template>
                                <template v-else-if="node.status === 'error'"><i
                                        class="el-step__icon-inner is-status el-icon-close"></i></template>
                                <template v-else>{{ data.nodes.indexOf(node) + 1 }}</template>
                            </div>
                            <div class="el-step__main">
                                <div class="step-label">{{ node.label }}</div>
                                <div class="step-description" :class="'step-' + node.status">
                                    <slot :name="`${node.id}`" :node="node">
                                        {{ node.description }}
                                    </slot>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'BranchSteps',
    props: {
        statusColors:{
            type: Object,
            required: false,
            default: () => ({
                wait: '#c0c4cc',
                process: '#303133',
                finish: '#67c23a',
                success: '#67c23a',
                error: '#f56c6c',
                half: '#93ea74',
            })
        },
        lineColors: {
            type: Object,
            required: true,
            default: () => ({})
        },
        data: {
            type: Object,
            required: true,
            default: () => ({
                nodes: [],
                rows: [],
                columns: []
            })
        }
    },
    data() {
        return {
            svgNS: 'http://www.w3.org/2000/svg',
        }
    },
    computed: {
        cssVars() {
            return {
                '--status-wait': this.statusColors.wait || '#c0c4cc',
                '--status-process': this.statusColors.process || '#303133',
                '--status-finish': this.statusColors.finish || '#67c23a',
                '--status-success': this.statusColors.success || '#67c23a',
                '--status-error': this.statusColors.error || '#f56c6c',
                '--status-half': this.statusColors.half || '#93ea74',
                // 文字色与图标/连线色解耦：默认绿色，仅当页面显式传 finishText/successText 时覆盖（如 TEAM 紫色）
                '--label-finish': this.statusColors.finishText || '#67c23a',
                '--label-success': this.statusColors.successText || '#67c23a',
            };
        }
    },
    async mounted() {
        this.$nextTick(() => {
            this.drawConnections();
            this._initPositionObservers();
        });
        // 监听窗口变化也要重绘
        window.addEventListener('resize', this._onWindowResize);
    },
    beforeDestroy() { // Vue 2
        this._disconnectPositionObservers();
        window.removeEventListener('resize', this._onWindowResize);
    },
    watch: {
        data: {
            handler() {
                this.$nextTick(() => {
                    this.drawConnections();
                });
            },
            deep: true
        },
        statusColors: {
            handler() {
                // 当 statusColors 改变时，强制重绘（样式通过 cssVars 生效）
                this.$nextTick(() => {
                    this.drawConnections();
                });
            },
            deep: true
        }
    },
    methods: {
        getNodeLevel(node) {
            return this.computeNodeLevels(this.data.nodes)[node.id];
        },
        _initPositionObservers() {
            this._disconnectPositionObservers();

            if (!this.$el || typeof this.$el.querySelector !== 'function') return;
            const container = this.$el.querySelector('.steps-container');
            if (!container) return;

            // ResizeObserver 用于检测节点尺寸/位置变化（当元素布局变化会触发）
            const RO = window.ResizeObserver;
            if (typeof RO === 'function') {
                // 监听容器尺寸变化
                try {
                    this._containerObserver = new RO(() => {
                        this.$nextTick(this.drawConnections);
                    });
                    this._containerObserver.observe(container);
                } catch (e) {
                    this._containerObserver = null;
                }

                // 为每个 step-icon-container 创建 observer
                const iconEls = container.querySelectorAll('.step-icon-container');
                iconEls.forEach(el => {
                    try {
                        const ro = new RO(() => {
                            // 小延迟合并多次变化
                            this.$nextTick(this.drawConnections);
                        });
                        ro.observe(el);
                        this._resizeObservers.push(ro);
                    } catch (e) {
                        // ignore
                    }
                });
            } else {
                // 浏览器不支持 ResizeObserver，退回到 MutationObserver + 轮询
                // MutationObserver 用于结构/属性变化
                try {
                    this._mutationObserver = new MutationObserver(() => {
                        this.$nextTick(this.drawConnections);
                    });
                    this._mutationObserver.observe(container, { attributes: true, childList: true, subtree: true });
                } catch (e) {
                    this._mutationObserver = null;
                }
                // 轮询作为最后保险（每 300ms 检查）
                if (!this._positionPoller) {
                    this._positionPoller = setInterval(() => {
                        this.drawConnections();
                    }, 300);
                }
            }
        },

        // 断开/清理观察器
        _disconnectPositionObservers() {
            if (this._containerObserver && typeof this._containerObserver.disconnect === 'function') {
                this._containerObserver.disconnect();
                this._containerObserver = null;
            }
            if (Array.isArray(this._resizeObservers)) {
                this._resizeObservers.forEach(ro => {
                    try { ro.disconnect(); } catch (e) { }
                });
                this._resizeObservers = [];
            }
            if (this._mutationObserver) {
                try { this._mutationObserver.disconnect(); } catch (e) { }
                this._mutationObserver = null;
            }
            if (this._positionPoller) {
                clearInterval(this._positionPoller);
                this._positionPoller = null;
            }
        },

        // 重新初始化（通常在 DOM 结构改变后）
        _reinitPositionObservers() {
            // 等 DOM 完全刷新后重建
            this.$nextTick(() => {
                this._initPositionObservers();
            });
        },

        // 窗口 resize 回调
        _onWindowResize() {
            this.$nextTick(this.drawConnections);
        },
        computeNodeLevels(nodes) {
            const levelMap = {};
            const inDegree = {};
            const maxPrevLevel = {};
            const graph = {};

            nodes.forEach(node => {
                const id = node.id;
                inDegree[id] = node.previous.length;
                maxPrevLevel[id] = -1;
                levelMap[id] = 0;
                graph[id] = node.next;
            });

            const queue = [];
            nodes.forEach(node => {
                if (inDegree[node.id] === 0) {
                    queue.push(node.id);
                    levelMap[node.id] = 0;
                }
            });

            while (queue.length > 0) {
                const currentId = queue.shift();
                const currentLevel = levelMap[currentId];
                for (const nextId of graph[currentId]) {
                    maxPrevLevel[nextId] = Math.max(maxPrevLevel[nextId], currentLevel + 1);
                    inDegree[nextId]--;
                    if (inDegree[nextId] === 0) {
                        levelMap[nextId] = maxPrevLevel[nextId];
                        queue.push(nextId);
                    }
                }
            }
            return levelMap;
        },
        getNodeById(id) {
            return this.data.nodes.find(node => node.id === id);
        },
        drawConnections() {
            const svg = this.$refs.svgLines;
            if (!svg || !this.$el || typeof this.$el.querySelector !== 'function') return;
            while (svg.firstChild) {
                svg.removeChild(svg.firstChild);
            }
            const containerRect = this.$el.querySelector('.steps-container').getBoundingClientRect();
            svg.setAttribute('width', containerRect.width);
            svg.setAttribute('height', containerRect.height);

            this.data.nodes.forEach(sourceNode => {
                sourceNode.next.forEach(targetId => {
                    const targetNode = this.getNodeById(targetId);
                    if (targetNode) {
                        const fromNode = this.$el.querySelector(`#${sourceNode.id}`).getBoundingClientRect();
                        const toNode = this.$el.querySelector(`#${targetId}`).getBoundingClientRect();
                   
                        const from = {
                            x: fromNode.left + fromNode.width - containerRect.left,
                            y: fromNode.top + fromNode.height / 2 - containerRect.top,
                        }
                        const to = {
                            x: toNode.left - containerRect.left,
                            y: toNode.top + toNode.height / 2 - containerRect.top,
                        }
                        const path = document.createElementNS(this.svgNS, "path");
                        if (sourceNode.next.length > 1 || targetNode.previous.length > 1) {
                            const controlPointX = from.x + (to.x - from.x) * 0.95;
                            const controlPointY1 = from.y;
                            const controlPointY2 = to.y;
                            const pathData = `M${from.x} ${from.y} C${controlPointX} ${controlPointY1}, ${controlPointX} ${controlPointY2}, ${to.x} ${to.y}`;
                            path.setAttribute("d", pathData);
                        } else {
                            path.setAttribute("d", `M${from.x} ${from.y} L${to.x} ${to.y}`);
                        }
                        const color = this.lineColors[sourceNode.status][targetNode.status];
                        path.setAttribute("stroke", color);
                        path.setAttribute("fill", "none");
                        path.setAttribute("stroke-width", "2");
                        path.setAttribute("class", "step-connection");
                        svg.appendChild(path);
                    }
                });
            });
        }
    }
}
</script>
<style>
.is-flex {
    flex-basis: auto !important;
    flex-shrink: 0;
    flex-grow: 0;
}
.branch-steps {
    position: relative;
    width: 100%;
    margin: 0 auto;
}

.steps-container {
    position: relative;
}

.step-node {
    display: flex;
    flex-direction: column;
    z-index: 2;
    transition: all 0.3s ease;
}

.step-icon-container {
    position: relative;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background-color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: bold;
    z-index: 1;
}
.el-step__main {
    white-space: normal;
    text-align: left;
}
.step-wait .step-icon-container {
    border: 2px solid var(--status-wait, #c0c4cc);
    color: var(--status-wait, #c0c4cc);
}
.step-wait .step-label {
    color: var(--status-wait, #c0c4cc);
}
.step-process .step-icon-container {
    border: 2px solid var(--status-process, #303133);
    background-color: #ffffff;
    color: var(--status-process, #303133);
    box-shadow: 0 0 8px rgba(64, 158, 255, 0.4);
}
.step-process .step-label {
    color: #303133;
    font-weight: bold;
}
.step-finish .step-icon-container {
    border: 2px solid var(--status-finish, #67c23a);
    background-color: #ffffff;
    color: var(--status-finish, #67c23a);
}
.step-finish .step-label {
    color: var(--label-finish, #67c23a);
}
.el-step__icon-inner {
    -webkit-user-select: none;
    -moz-user-select: none;
    user-select: none;
    text-align: center;
    font-weight: 700;
    line-height: 1;
    color: inherit;
}
.step-success .step-icon-container {
    border: 2px solid var(--status-finish, #67c23a);
    background-color: #ffffff;
    color: var(--status-finish, #67c23a);
}
.step-success .step-label {
    color: var(--label-success, #67c23a);
}
.step-error .step-icon-container {
    border: 2px solid var(--status-error, #f56c6c);
    background-color: #ffffff;
    color: var(--status-error, #f56c6c);
}
.step-error .step-label {
    color: #f56c6c;
}
.step-half .step-icon-container {
    border: 2px solid var(--status-half, #93ea74);
    background-color: #ffffff;
    color: var(--status-half, #93ea74);
}
.step-half .step-label {
    color: #93ea74;
}
.step-label {
    margin-top: 8px;
    font-size: 16px;
    white-space: nowrap;
}
.step-description {
    margin-top: 6px;
    font-size: 12px;
        white-space: nowrap;
    /* 父容器文字颜色由状态控制，供子元素继承 */
    color: inherit;
    transition: all .2s;
}

/* 状态样式（父容器设置背景与颜色） */
.step-description.step-wait,
.step-wait .step-description {
    color: #c0c4cc;
}

.step-description.step-process,
.step-process .step-description {
    color: #303133;
}

.step-description.step-finish,
.step-finish .step-description {
    color: var(--label-finish, #67c23a);
}

.step-description.step-success,
.step-success .step-description {
    color: var(--label-success, #67c23a);
}

.step-description.step-error,
.step-error .step-description {
    color: #f56c6c;
}

.step-description.step-half,
.step-half .step-description {
    color: #93ea74;
}

/* 仅继承字体颜色到插槽内容，确保插槽内元素不继承父背景 */
.step-description * {
    color: inherit !important;
    background: transparent !important;
    background-color: transparent !important;
}

.node-lines {
    position: absolute;
    top: 0;
    left: 0;

    z-index: 1;
}
.node-lines,
.node-lines .step-connection,
.step-connection {
    pointer-events: none;
}

/* 确保描述区及其子元素可以响应交互（按钮/链接等） */
.step-description,
.step-description * {
    pointer-events: auto !important;
}

/* 若需要提高节点层级，确保节点在 SVG 之上 */
.step-node {
    z-index: 3;
}
</style>